import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronLeft, ChevronRight, Search, Loader2, Trash2 } from 'lucide-react'
import { get, post, del } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { NotificationItem } from '@/components/NotificationBell'
import { FilterTabs } from '@/components/ui/filter-tabs'
import { EmptyState } from '@/components/ui/empty-state'
import { IndexBadge } from '@/components/ui/data-table'
import { ANNOUNCE_KIND, announceKind, toAnnouncement, type AnnouncementContent } from '@/components/announce/AnnouncementView'
import { AnnouncementSheet, refreshNotifications } from '@/components/announce/AnnouncementSheet'

/**
 * Mỗi loại thông báo có một NHÃN và một mức ý nghĩa. Không có icon riêng:
 * nhãn ("Giao thành công", "Cảnh báo cứu đơn") đã nói đúng điều mà icon
 * định nói, và 7 hue hardcode cũ đụng nghĩa với bảng màu semantic.
 */
function getNotificationVisual(item: Pick<NotificationItem, 'type' | 'data'>) {
  switch (item.type) {
    // Thông báo cấp trên gửi: nhãn theo loại người gửi chọn (Cảnh báo / Khuyến mãi / Thông báo)
    case 'announcement': {
      const k = ANNOUNCE_KIND[announceKind(item.data?.kind)]
      return { badge: k.label, tone: k.badge }
    }
    case 'order_created':
      return { badge: 'Đơn hàng mới', tone: 'bg-info/10 text-info' }
    // Tin nhắn / bình luận mới của khách trong Gom Đơn
    case 'gomdon_message':
      return { badge: 'Gom Đơn', tone: 'bg-accent/10 text-accent' }
    case 'order_delivered':
      return { badge: 'Giao thành công', tone: 'bg-ok/10 text-ok' }
    case 'order_failed':
      return { badge: 'Cảnh báo cứu đơn', tone: 'bg-danger/10 text-danger' }
    case 'order_returned':
      return { badge: 'Chuyển hoàn', tone: 'bg-warn/10 text-warn' }
    case 'reconcile_ready':
      return { badge: 'Đối soát', tone: 'bg-ok/10 text-ok' }
    case 'commission_paid':
      return { badge: 'Chiết khấu', tone: 'bg-accent/10 text-accent' }
    case 'commission_missing':
      return { badge: 'Thiếu chiết khấu', tone: 'bg-warn/10 text-warn' }
    case 'settlement_issued':
    case 'settlement_auto_confirmed':
      return { badge: 'Bảng kê', tone: 'bg-accent/10 text-accent' }
    case 'payout_confirmed':
      return { badge: 'Đã chuyển tiền', tone: 'bg-ok/10 text-ok' }
    case 'reconcile_variance':
    case 'remittance_unmatched':
    case 'unauthorized_outflow':
    case 'wallet_negative':
      return { badge: 'Cần kiểm tra', tone: 'bg-danger/10 text-danger' }
    case 'dispute_raised':
    case 'dispute_escalated':
    case 'dispute_resolved':
      return { badge: 'Khiếu nại', tone: 'bg-warn/10 text-warn' }
    case 'statement_imported':
      return { badge: 'Đối soát', tone: 'bg-info/10 text-info' }
    case 'login_qr':
      return { badge: 'Đăng nhập', tone: 'bg-info/10 text-info' }
    case 'system':
    default:
      return { badge: 'Hệ thống', tone: 'bg-paperSoft text-inkSoft' }
  }
}

// Mỗi tab gom cả nhóm loại thông báo (trước đây tab Đối soát chỉ lọc
// 'reconcile_ready' — loại đã ngừng phát sinh — nên luôn trống).
const TAB_TYPES = {
  upper: ['announcement'],
  order: ['order_created', 'order_delivered', 'order_failed', 'order_returned', 'gomdon_message'],
  finance: [
    'reconcile_ready',
    'commission_paid',
    'commission_missing',
    'statement_imported',
    'reconcile_variance',
    'remittance_unmatched',
    'unauthorized_outflow',
    'settlement_issued',
    'settlement_auto_confirmed',
    'dispute_raised',
    'dispute_escalated',
    'dispute_resolved',
    'payout_confirmed',
    'wallet_negative',
  ],
  system: ['system', 'approval_request', 'approval_result', 'sepay_webhook_silent', 'login_qr'],
} as const

export default function Notifications() {
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | keyof typeof TAB_TYPES>('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [reading, setReading] = useState<AnnouncementContent | null>(null)
  const limit = 15

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Fetch danh sách thông báo
  const { data, isLoading } = useQuery({
    queryKey: ['notifications-all', page, activeTab],
    queryFn: () => {
      let url = `/notifications?page=${page}&limit=${limit}`
      if (activeTab === 'unread') {
        url += '&unreadOnly=true'
      } else if (activeTab !== 'all') {
        url += `&type=${TAB_TYPES[activeTab].join(',')}`
      }
      return get<{
        items: NotificationItem[]
        pagination: { page: number; limit: number; total: number; totalPages: number; unreadCount: number }
      }>(url)
    },
    staleTime: 10_000,
  })

  // Mutation đánh dấu tất cả đã đọc
  const markAllMutation = useMutation({
    mutationFn: () => post('/notifications/read-all'),
    onSuccess: () => refreshNotifications(queryClient),
  })

  // Mutation đánh dấu 1 item đã đọc
  const markReadMutation = useMutation({
    mutationFn: (id: string) => post(`/notifications/${id}/read`),
    onSuccess: () => refreshNotifications(queryClient),
  })

  // Mutation xóa 1 item
  const deleteMutation = useMutation({
    mutationFn: (id: string) => del(`/notifications/${id}`),
    onSuccess: () => refreshNotifications(queryClient),
  })

  const items = data?.items || []
  const q = search.trim().toLowerCase()
  const filteredItems = q
    ? items.filter(
        (it) =>
          (it.title ?? '').toLowerCase().includes(q) ||
          (it.message ?? '').toLowerCase().includes(q) ||
          (it.type === 'announcement' && typeof it.data?.sender === 'string' && it.data.sender.toLowerCase().includes(q))
      )
    : items

  const openAnnouncement = (item: NotificationItem) => {
    if (!item.isRead) markReadMutation.mutate(item.id)
    setReading(toAnnouncement(item))
  }

  const pagination = data?.pagination

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* ── Top Header ────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1>Thông báo</h1>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllMutation.mutate()}
            disabled={markAllMutation.isPending || !pagination?.unreadCount}
            className="h-11 desktop:h-9 gap-1.5 text-xs font-medium"
          >
            <span>{markAllMutation.isPending ? 'Đang đánh dấu…' : 'Đánh dấu tất cả đã đọc'}</span>
          </Button>

          <Link to="/settings">
            <Button variant="ghost" size="sm" className="h-11 desktop:h-9 gap-1.5 text-xs text-muted hover:text-ink">
              <span>Cài đặt thông báo</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* ── Filter Bar & Search ───────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-b border-rule pb-3">
        <FilterTabs
          value={activeTab}
          onChange={(v) => {
            setActiveTab(v as any)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'Tất cả' },
            { value: 'unread', label: 'Chưa đọc', count: pagination?.unreadCount },
            { value: 'upper', label: 'Cấp trên' },
            { value: 'order', label: 'Đơn hàng' },
            { value: 'finance', label: 'Đối soát' },
            { value: 'system', label: 'Hệ thống' },
          ]}
        />

        {/* Search Box */}
        <div className="relative w-full sm:w-64 shrink-0">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm kiếm nội dung..."
            className="w-full pl-9 pr-3 py-1.5 text-xs bg-raise border border-rule rounded-lg text-ink placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      </div>

      {/* ── List Content ──────────────────────────────────────────── */}
      <div className="bg-raise rounded-xl overflow-hidden shadow-card">
        {isLoading ? (
          <div className="py-20 flex flex-col items-center justify-center text-muted gap-2">
            <Loader2 className="size-6 animate-spin text-accent" />
            <span className="text-xs">Đang tải…</span>
          </div>
        ) : filteredItems.length > 0 ? (
          <div className="divide-y divide-rule">
            {filteredItems.map((item, i) => {
              const { badge: badgeText, tone } = getNotificationVisual(item)
              // Thông báo cấp trên: cả dòng bấm được (nút ở tiêu đề phủ cả dòng) → khung đọc đầy đủ
              const isAnnouncement = item.type === 'announcement'
              const sender = isAnnouncement && typeof item.data?.sender === 'string' ? item.data.sender : null
              return (
                <div
                  key={item.id}
                  className={cn(
                    'flex items-start gap-4 p-4 sm:p-5 hover:bg-paperSoft/60 transition-colors relative group',
                    !item.isRead && 'bg-accent/[0.03]'
                  )}
                >
                  {/* Text Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <IndexBadge n={(page - 1) * limit + i + 1} />
                      <span className={cn('rounded-full px-2 py-0.5 text-xs', tone)}>{badgeText}</span>
                      <h2 className={cn('max-w-[calc(100%-1rem)] text-sm font-medium text-ink truncate', !item.isRead && 'font-bold')}>
                        {isAnnouncement ? (
                          <button
                            type="button"
                            onClick={() => openAnnouncement(item)}
                            className="block max-w-full truncate text-left after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:after:rounded-lg focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-accent"
                          >
                            {item.title}
                          </button>
                        ) : (
                          item.title
                        )}
                      </h2>
                      {!item.isRead && (
                        <span className="size-2 rounded-full bg-accent shrink-0" title="Chưa đọc" />
                      )}
                    </div>

                    <p className={cn('text-xs sm:text-sm text-inkSoft leading-relaxed whitespace-pre-line', isAnnouncement && 'line-clamp-2 break-words')}>
                      {item.message}
                    </p>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted">
                      {sender && <span className="font-medium text-inkSoft">{sender}</span>}
                      <span>{new Date(item.createdAt).toLocaleString('vi-VN')}</span>

                      {!isAnnouncement && item.data?.url && (
                        <button
                          type="button"
                          onClick={() => {
                            if (!item.isRead) markReadMutation.mutate(item.id)
                            navigate(item.data!.url)
                          }}
                          className="text-accent hover:underline inline-flex items-center gap-1 font-medium"
                        >
                          <span>Chi tiết</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Actions — nằm trên nút phủ dòng của thông báo cấp trên */}
                  <div className="relative z-10 flex items-center gap-1 shrink-0 opacity-80 group-hover:opacity-100 transition-opacity">
                    {!item.isRead && (
                      <button
                        type="button"
                        onClick={() => markReadMutation.mutate(item.id)}
                        disabled={markReadMutation.isPending}
                        aria-label="Đánh dấu đã đọc"
                        title="Đánh dấu đã đọc"
                        className="flex size-11 items-center justify-center rounded-lg text-muted hover:text-accent hover:bg-accent/10 transition-colors desktop:size-9"
                      >
                        <Check className="size-4" aria-hidden />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(item.id)}
                      disabled={deleteMutation.isPending}
                      aria-label="Xóa"
                      title="Xóa"
                      className="flex size-11 items-center justify-center rounded-lg text-muted hover:text-danger hover:bg-danger/10 transition-colors desktop:size-9"
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <EmptyState title="Chưa có thông báo nào." />
        )}

        {/* ── Pagination Footer ─────────────────────────────────────── */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-rule bg-paperSoft/30 text-xs text-muted">
            <span>
              Trang {pagination.page} / {pagination.totalPages} (Tổng {pagination.total} thông báo)
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="h-8 px-2.5"
              >
                <ChevronLeft className="size-3.5" />
                <span className="hidden sm:inline ml-1">Trước</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page >= pagination.totalPages}
                className="h-8 px-2.5"
              >
                <span className="hidden sm:inline mr-1">Sau</span>
                <ChevronRight className="size-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <AnnouncementSheet a={reading} onClose={() => setReading(null)} />
    </div>
  )
}

